import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useGardenPageTitle } from '../useGardenPageTitle';
import { ArrowLeft, ScanLine, Image as ImageIcon, Pencil, ChevronRight } from 'lucide-react';

// Mobile-first chooser shown when the user taps "Add plant". One action per
// screen, large tap targets, minimal copy. Routes the user into one of three
// flows:
//   - Identify from photo: PlantNet → prefilled new plant form
//   - Add Manually:        existing PlantFormScreen
//   - Scan Plant Tag:      OCR + AI parse → confirm → save
export default function AddPlantChoiceScreen() {
  useGardenPageTitle('Add a plant', 'Add a plant');
  const navigate = useNavigate();

  return (
    <div className="pb-24">
      <header className="flex items-center gap-2 px-2 pt-3 pb-2">
        <button
          onClick={() => navigate(-1)}
          className="min-h-11 min-w-11 rounded-full flex items-center justify-center text-garden-muted"
          aria-label="Back"
        >
          <ArrowLeft size={22} />
        </button>
        <h1 className="flex-1 text-lg font-semibold text-garden-ink truncate">
          Add a plant
        </h1>
      </header>

      <div className="px-4 pt-2 space-y-3">
        <ChoiceCard
          Icon={ImageIcon}
          title="Identify from photo"
          subtitle="Snap a leaf or flower — we suggest the name"
          onClick={() => navigate('/garden/plants/identify')}
        />
        <ChoiceCard
          Icon={Pencil}
          title="Add Manually"
          subtitle="Type in the details yourself"
          onClick={() => navigate('/garden/plants/new')}
        />
        <ChoiceCard
          Icon={ScanLine}
          title="Scan Plant Tag"
          subtitle="Snap the tag — we'll read it for you"
          onClick={() => navigate('/garden/plants/scan')}
        />
      </div>
    </div>
  );
}

function ChoiceCard({ Icon, title, subtitle, onClick, primary, muted }) {
  const iconBg = primary
    ? 'bg-garden-primary text-white'
    : muted
      ? 'bg-black/5 text-garden-muted'
      : 'bg-garden-primaryLight text-garden-primaryDark';

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left rounded-2xl bg-white border border-black/5 shadow-sm p-4 flex items-center gap-4 min-h-[84px] active:scale-[0.99] transition hover:border-garden-primary/30 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-garden-primary"
    >
      <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 ${iconBg}`}>
        <Icon size={28} strokeWidth={2} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-garden-ink text-base">{title}</div>
        <div className="text-sm text-garden-muted truncate">{subtitle}</div>
      </div>
      <ChevronRight size={20} className="text-garden-muted shrink-0" />
    </button>
  );
}
